export type RespirationRef = {
  epoch_id?: string | null
  status: string
  rpm: number | null
  reason?: string | null
  lf_band_overlap?: boolean
}

export const RESPIRATION_LABEL = 'frecuencia respiratoria estimada desde la variabilidad RR (RSA); no es respiración medida'

const reasons: Record<string, string> = {
  too_few_beats: 'pocos latidos RR registrados',
  insufficient_rr_coverage: 'cobertura RR insuficiente',
  excessive_artifacts: 'demasiados latidos con artefacto',
  nyquist_limited: 'la HR no permite resolver esa respiración',
  no_dominant_peak: 'sin pico dominante en la banda respiratoria',
  epoch_too_short: 'época menor a 30 s',
  clock_not_verified: 'alineación temporal no verificada',
  missing_explicit_baseline: 'sin referencia inicial explícita',
  quality_checks_failed: 'no pasa los controles de calidad',
  lf_band_overlap: 'pico en banda LF, indistinguible de oscilación lenta no respiratoria',
  epoch_not_defined: 'época no definida',
  respiration_not_in_gold_version: 'método no calculado en esta versión',
}

const rate = (v: number) => `${v.toLocaleString('es-AR', {maximumFractionDigits: 1})} rpm`

function side(ref?: RespirationRef | null) {
  if (!ref || ref.rpm == null || ref.status === 'not_estimable')
    return `no estimable (${reasons[ref?.reason ?? ''] ?? ref?.reason?.replaceAll('_', ' ') ?? 'sin dato'})`
  // A slow LF peak is never shown as if it were a breathing rate.
  if (ref.lf_band_overlap) return `posible oscilación lenta, no respiración (pico ${rate(ref.rpm)}, ambiguo)`
  return ref.status === 'estimated' ? rate(ref.rpm) : `${rate(ref.rpm)} (ambigua, no es valor firme)`
}

function overall(early?: RespirationRef | null, late?: RespirationRef | null) {
  const states = [early?.status ?? 'not_estimable', late?.status ?? 'not_estimable']
  if (states.every(s => s === 'estimated')) return 'estimada'
  if (states.includes('not_estimable')) return states.some(s => s !== 'not_estimable') ? 'parcial' : 'no estimable'
  return 'ambigua'
}

/** Backend RSA result next to an HR change: values or concrete reasons, never a colour. */
export function RespirationLine({early, late, label, className = 'text-xs text-text-secondary'}: {early?: RespirationRef | null; late?: RespirationRef | null; label?: string | null; className?: string}) {
  return <p className={className}>
    Respiración estimada (RSA): {side(early)} → {side(late)} · {overall(early, late)}.{' '}
    <span className="text-text-muted">{label ?? RESPIRATION_LABEL}.</span>
  </p>
}
